/**
 * TutorialContext - Global state management for tutorial system
 * Manages active tutorial, loading states, and tutorial operations
 */

import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import tutorialService from '../services/tutorialService';
import logger from '../utils/logger';
import { useAuth } from './AuthContext';
import { useAppData } from './AppDataContext';

const TutorialContext = createContext();

export const TutorialProvider = ({ children }) => {
  const [activeTutorial, setActiveTutorial] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasPendingTutorials, setHasPendingTutorials] = useState(false);
  const [tutorialServiceInstance] = useState(() => tutorialService);
  const { user } = useAuth();
  const { initialized: appInitialized } = useAppData();

  // Ref that always holds the latest activeTutorial value so callbacks created
  // inside Driver.js (which capture a stale closure) can read the current state.
  const activeTutorialRef = useRef(null);
  useEffect(() => { activeTutorialRef.current = activeTutorial; }, [activeTutorial]);

  // Guard: only initialize tutorials once per authenticated session.
  // Prevents StrictMode double-invocation and loading-state flips from
  // triggering repeated tutorial API calls.
  const hasInitialized = useRef(false);

  // Load tutorials and progress only after AppDataContext has completed its
  // startup fetch (appInitialized=true). This ensures tutorial API calls don't
  // compete with the critical /me/context request.
  useEffect(() => {
    if (!user) {
      logger.debug('TutorialContext: User not authenticated, skipping tutorial initialization');
      setIsLoading(false);
      hasInitialized.current = false;
      return;
    }

    // Wait until startup context has fully loaded
    if (!appInitialized) {
      return;
    }

    // Skip if already initialized for this session
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    const initializeTutorials = async () => {
      try {
        setIsLoading(true);
        logger.debug('TutorialContext: Initializing tutorial system');

        // Load pending tutorials + progress in one call
        const { pending: pendingTutorials } = await tutorialService.loadStartupData();

        setHasPendingTutorials(pendingTutorials.length > 0);
        logger.debug(`TutorialContext: Initialized with ${pendingTutorials.length} pending tutorials`);
      } catch (error) {
        logger.error('TutorialContext: Failed to initialize tutorials:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeTutorials();
  }, [user, appInitialized]);

  // Check if tutorial should be shown (no API call)
  const shouldShowTutorial = useCallback((key, userContext = {}) => {
    return tutorialService.shouldShowTutorial(key, userContext);
  }, []);

  // Start tutorial from correct step
  const startTutorial = useCallback((key) => {
    const tutorial = tutorialService.getTutorial(key);
    if (tutorial) {
      logger.debug(`TutorialContext: Starting tutorial ${key} from step ${tutorial.current_step || 0}`);
      setActiveTutorial({
        ...tutorial,
        currentStep: tutorial.current_step || 0
      });
      tutorialService.updateProgress(key, 'in_progress', tutorial.current_step || 0);
    } else {
      logger.warn(`TutorialContext: Tutorial ${key} not found`);
    }
  }, []);

  // Complete/skip tutorial
  const completeTutorial = useCallback((key, status = 'completed') => {
    logger.debug(`TutorialContext: Completing tutorial ${key} with status ${status}`);
    tutorialService.updateProgress(key, status);
    setActiveTutorial(null);
    setHasPendingTutorials(tutorialService.hasPendingTutorials());
  }, []);

  // Navigate to specific step in tutorial
  const goToStep = useCallback((stepIndex) => {
    // Use the ref so this is safe to call from stale Driver.js callbacks — the
    // ref always reflects whether the tutorial is still active at call time.
    const current = activeTutorialRef.current;
    if (!current) return;
    if (stepIndex < 0 || stepIndex >= current.steps.length) return;

    logger.debug(`TutorialContext: Navigating to step ${stepIndex}`);
    tutorialService.updateProgress(current.tutorial_key, 'in_progress', stepIndex);
    // Guard the state update: if the tutorial was cleared between the ref check
    // and this setState, keep it null rather than resurrecting a partial object.
    setActiveTutorial(prev => prev ? { ...prev, currentStep: stepIndex } : prev);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pause tutorial
  const pauseTutorial = useCallback(() => {
    if (activeTutorial) {
      logger.debug(`TutorialContext: Pausing tutorial ${activeTutorial.tutorial_key}`);
      tutorialService.updateProgress(activeTutorial.tutorial_key, 'pending', activeTutorial.currentStep);
      setActiveTutorial(null);
    }
  }, [activeTutorial]);

  // Resume tutorial
  const resumeTutorial = useCallback((key) => {
    const tutorial = tutorialService.getTutorial(key);
    if (tutorial && tutorial.status === 'pending') {
      logger.debug(`TutorialContext: Resuming tutorial ${key} from step ${tutorial.current_step || 0}`);
      startTutorial(key);
    }
  }, [startTutorial]);

  // Reset all progress
  const resetAllProgress = useCallback(async () => {
    logger.debug('TutorialContext: Resetting all tutorial progress');
    await tutorialService.resetProgress();
    setActiveTutorial(null);
    setHasPendingTutorials(false);
    
    // Reload tutorials
    try {
      const pendingTutorials = await tutorialService.loadPendingTutorials();
      setHasPendingTutorials(pendingTutorials.length > 0);
    } catch (error) {
      logger.error('TutorialContext: Failed to reload tutorials after reset:', error);
    }
  }, []);

  // Get current step information
  const getCurrentStep = useCallback(() => {
    if (!activeTutorial || !activeTutorial.steps || activeTutorial.currentStep >= activeTutorial.steps.length) {
      return null;
    }
    return activeTutorial.steps[activeTutorial.currentStep];
  }, [activeTutorial]);

  // Check if current step is the last step
  const isLastStep = useCallback(() => {
    if (!activeTutorial || !activeTutorial.steps) {
      return false;
    }
    return activeTutorial.currentStep >= activeTutorial.steps.length - 1;
  }, [activeTutorial]);

  // Get total steps in current tutorial
  const getTotalSteps = useCallback(() => {
    return activeTutorial?.steps?.length || 0;
  }, [activeTutorial]);

  // Get progress percentage
  const getProgressPercentage = useCallback(() => {
    if (!activeTutorial || !activeTutorial.steps) {
      return 0;
    }
    return Math.round(((activeTutorial.currentStep + 1) / activeTutorial.steps.length) * 100);
  }, [activeTutorial]);

  // Refresh tutorials from server
  const refreshTutorials = useCallback(async () => {
    logger.debug('TutorialContext: Refreshing tutorials from server');
    tutorialService.clearCache();
    
    try {
      const [pendingTutorials] = await Promise.all([
        tutorialService.loadPendingTutorials(),
        tutorialService.loadUserProgress()
      ]);
      
      setHasPendingTutorials(pendingTutorials.length > 0);
      logger.debug(`TutorialContext: Refreshed with ${pendingTutorials.length} pending tutorials`);
    } catch (error) {
      logger.error('TutorialContext: Failed to refresh tutorials:', error);
    }
  }, []);

  const value = {
    // State
    activeTutorial,
    isLoading,
    hasPendingTutorials,
    
    // Tutorial operations
    shouldShowTutorial,
    startTutorial,
    completeTutorial,
    pauseTutorial,
    resumeTutorial,
    resetAllProgress,
    refreshTutorials,
    
    // Navigation
    goToStep,
    
    // Helpers
    getCurrentStep,
    isLastStep,
    getTotalSteps,
    getProgressPercentage,
    
    // Service instance (for advanced usage)
    tutorialService: tutorialServiceInstance
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

export default TutorialContext;
